import { useEffect, useState } from "react";

import useContractPreview from "./use-contract-preview";
import usePdfViewer from "./use-pdf-viewer";
import { PREVIEW_KIND, REVIEW_HINT, VIEWER_STATUS } from "../misc/constants";
import { previewKindOfType } from "../misc/helpers";

/**
 * The review-and-sign step, shared by the brand and publisher portals. Both
 * must be shown the contract before they can sign it — only the endpoints
 * differ.
 *
 * The document shown is the one `documentPath` serves (the role's /document
 * route, fetched with `base`), since that route needs the bearer token.
 *
 * `readOnly` drops the signing controls, for a contract kept openable after
 * it has been signed.
 *
 * `blockedHint`, when set, keeps the acknowledgement locked for a reason the
 * server enforces rather than the review itself — the document stays
 * reviewable, and the hint says what it is waiting on.
 * @param options
 */
const useContractReview = ({ contract, base, documentPath, onAcknowledge, readOnly = false, blockedHint = "" }) => {
    const [open, setOpen] = useState(false);
    const [acknowledged, setAcknowledged] = useState(false);
    // Set when the file is opened in the recipient's own reader, which is the
    // only way to review a document the browser cannot embed.
    const [openedExternally, setOpenedExternally] = useState(false);

    const active = open && Boolean(contract?.exists);

    const preview = useContractPreview({ base, path: documentPath, active });

    // The contract as the modal renders it: the payload's details, with the
    // served document in place of the stored URL. While the document is
    // still loading it is taken to be a PDF, which is what the route serves.
    const shown = contract && {
        ...contract,
        url        : preview.url,
        previewKind: preview.url ? previewKindOfType(preview.type) : PREVIEW_KIND.PDF
    };

    const pdfViewer = usePdfViewer({
        url : preview.url,
        kind: shown?.previewKind,
        active
    });

    // A failed document request reads as the frame failing: same fallback,
    // with the server's reason in place of the generic one.
    const viewer = {
        ...pdfViewer,
        loading: preview.loading,
        error  : preview.error,
        status : preview.error ? VIEWER_STATUS.ERROR : pdfViewer.status,
        retry  : preview.error ? preview.retry : pdfViewer.retry
    };

    const isPdf = shown?.previewKind === PREVIEW_KIND.PDF;
    const unreadable = shown?.previewKind === PREVIEW_KIND.NONE;

    // Acknowledgement is only offered once the contract has genuinely been put
    // in front of the recipient, and only two things can vouch for that:
    //   - the frame reported the document displayed, which is all that is
    //     observable across an origin; or
    //   - the file was opened in their own reader.
    const canAcknowledge = (() => {
        if (!contract?.exists || unreadable || blockedHint) return false;
        if (!isPdf) return openedExternally;

        return viewer.status === VIEWER_STATUS.FRAME
            ? viewer.frameReady
            : openedExternally;
    })();

    const hint = () => {
        if (canAcknowledge) return "";
        if (blockedHint) return blockedHint;
        if (preview.error) return preview.error;
        if (unreadable) return REVIEW_HINT.BROKEN;
        if (!isPdf) return REVIEW_HINT.DOWNLOAD;

        // A frame that is still loading is the only other state left: once it
        // has loaded, acknowledgement is open and there is nothing to explain.
        return viewer.status === VIEWER_STATUS.ERROR
            ? REVIEW_HINT.DOWNLOAD
            : REVIEW_HINT.LOADING;
    };

    // The gate can close under an already-ticked box: the frame can time out,
    // or a refetch can swap the document, after the recipient has confirmed.
    // An acknowledgement must never outlive the evidence it rests on, so it is
    // dropped with the gate rather than left standing.
    useEffect(() => {
        if (!canAcknowledge) setAcknowledged(false);
    }, [canAcknowledge]);

    const close = () => {
        setOpen(false);
        setAcknowledged(false);
        setOpenedExternally(false);
    };

    // Reopening a contract always starts from scratch — the acknowledgement
    // has to be a deliberate act every time.
    const openReview = () => {
        setOpen(true);
        setAcknowledged(false);
        setOpenedExternally(false);
    };

    const handleAcknowledge = async () => {
        if (!canAcknowledge) return;

        const done = await onAcknowledge();
        if (done) close();
    };

    return {
        openReview,
        modal: {
            open               : active,
            contract           : shown,
            viewer,
            acknowledged,
            canAcknowledge,
            hint               : hint(),
            readOnly,
            onAcknowledgeChange: setAcknowledged,
            onOpenExternally   : () => setOpenedExternally(true),
            onAcknowledge      : handleAcknowledge,
            onClose            : close
        }
    };
};

export default useContractReview;
